import React, { useEffect, useState, useRef } from "react";
import "./assignTask.css";

function AssignTask() {
  const [projects, setProjects] = useState([]); // NEW: projects list
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({
    project_id: "",
    task_id: "",
    employee_id: "",
    role_in_task: "",
  });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const messageTimeoutRef = useRef(null);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  // 🔹 Helper fetch with token
  async function apiFetch(url, opts = {}) {
    const token = localStorage.getItem("token");
    const headers = { ...(opts.headers || {}) };
    if (opts.json !== false && opts.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers });
    const contentType = res.headers.get("content-type") || "";
    let parsed;
    try {
      parsed = contentType.includes("application/json")
        ? await res.json()
        : await res.text();
    } catch (err) {
      parsed = `Invalid response body: ${err.message}`;
    }
    return { ok: res.ok, status: res.status, body: parsed, contentType };
  }

  // 🔹 Fetch projects, employees (but NOT tasks: tasks load per-project)
  async function fetchMeta() {
    try {
      const [projectsRes, empRes] = await Promise.all([
        apiFetch(`${process.env.REACT_APP_API_URL}/api/projects`, {
          method: "GET",
          json: false,
        }),
        apiFetch(`${process.env.REACT_APP_API_URL}/api/employees`, {
          method: "GET",
          json: false,
        }),
      ]);

      if (!projectsRes.ok)
        throw new Error(projectsRes.body?.error || "Failed to load projects");
      if (!empRes.ok)
        throw new Error(empRes.body?.error || "Failed to load employees");

      setProjects(Array.isArray(projectsRes.body) ? projectsRes.body : []);
      setEmployees(Array.isArray(empRes.body) ? empRes.body : []);
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    }
  }

  // NEW: fetch tasks for a specific project
  async function fetchTasksForProject(projectId) {
    if (!projectId) {
      setTasks([]);
      setForm((f) => ({ ...f, task_id: "" }));
      setAssignments([]);
      return;
    }
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/tasks/project/${projectId}`,
        { method: "GET", json: false }
      );
      if (!ok)
        throw new Error(body?.error || "Failed to load tasks for project");
      setTasks(Array.isArray(body) ? body : []);
      // reset task selection + assignments when changing project
      setForm((f) => ({ ...f, task_id: "" }));
      setAssignments([]);
    } catch (err) {
      setTasks([]);
      setAssignments([]);
      showTempMessage(`❌ ${err.message}`);
    }
  }

  // fetch assignments for a task (endpoint kept as earlier)
  async function fetchAssignments(taskId) {
    if (!taskId) return setAssignments([]);
    try {
      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/task-assignments/task/${taskId}`,
        { method: "GET", json: false }
      );
      if (!ok) throw new Error(body?.error || "Failed to fetch assignments");
      setAssignments(Array.isArray(body) ? body : []);
    } catch (err) {
      setAssignments([]);
      showTempMessage(`❌ ${err.message}`);
    }
  }

  // Handle form change
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));

    // if project changed -> load tasks for that project
    if (name === "project_id") {
      // clear task and assignments while loading new ones
      setForm((f) => ({ ...f, project_id: value, task_id: "" }));
      fetchTasksForProject(value);
      return;
    }

    // if task changed -> load its assignments
    if (name === "task_id") {
      fetchAssignments(value);
    }
  }

  // Assign employee
  async function handleAssign(e) {
    e.preventDefault();
    if (!form.project_id) return showTempMessage("⚠️ Select a project first");
    if (!form.task_id || !form.employee_id) {
      return showTempMessage("⚠️ Task and Employee are required");
    }

    setLoading(true);
    try {
      const payload = {
        task_id: Number(form.task_id),
        employee_id: Number(form.employee_id),
        role_in_task: form.role_in_task || null,
        assigned_by: user?.id || null,
      };

      const { ok, body } = await apiFetch(
        `${process.env.REACT_APP_API_URL}/api/task-assignments`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );
      if (!ok) throw new Error(body?.error || "Failed to assign task");

      showTempMessage("✅ Task assigned successfully!");
      fetchAssignments(form.task_id);
      setForm((f) => ({ ...f, employee_id: "", role_in_task: "" }));
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  // Remove assignment (robust approach - try URL then fallback)
  async function handleRemove(taskId, empId) {
    if (!window.confirm("Remove this assignment?")) return;
    try {
      const endpointUrl = `${process.env.REACT_APP_API_URL}/api/task-assignments/${taskId}/assignments/${empId}`;
      let res = await apiFetch(endpointUrl, { method: "DELETE", json: false });

      if (!res.ok) {
        // fallback to DELETE with body if backend expects that
        res = await apiFetch(
          `${process.env.REACT_APP_API_URL}/api/task-assignments`,
          {
            method: "DELETE",
            body: JSON.stringify({ task_id: taskId, employee_id: empId }),
          }
        );
      }

      if (!res.ok)
        throw new Error(res.body?.error || "Failed to remove assignment");
      fetchAssignments(taskId);
      showTempMessage("✅ Assignment removed");
    } catch (err) {
      showTempMessage(`❌ ${err.message}`);
    }
  }

  function showTempMessage(text) {
    setMessage(text);
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = setTimeout(() => setMessage(""), 4000);
  }

  useEffect(() => {
    fetchMeta();
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="assign-task-container">
      <h2>Assign Task</h2>

      {message && (
        <div
          className={`assign-task-message ${
            message.startsWith("✅") ? "success" : "error"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleAssign} className="assign-form">
        {/* NEW: Project selector */}
        <select
          name="project_id"
          value={form.project_id}
          onChange={handleChange}
          required
        >
          <option value="">Select Project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        {/* Task selector shows tasks for selected project only */}
        <select
          name="task_id"
          value={form.task_id}
          onChange={handleChange}
          disabled={!form.project_id}
        >
          <option value="">
            {form.project_id ? "Select Task" : "Select Project first"}
          </option>
          {tasks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title} {t.milestone_title ? `— ${t.milestone_title}` : ""}
            </option>
          ))}
        </select>

        <select
          name="employee_id"
          value={form.employee_id}
          onChange={handleChange}
        >
          <option value="">Select Employee</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name} {e.position ? `(${e.position})` : ""}
            </option>
          ))}
        </select>

        <input
          type="text"
          name="role_in_task"
          placeholder="Role (e.g., Developer, Tester)"
          value={form.role_in_task}
          onChange={handleChange}
        />

        <button type="submit" disabled={loading}>
          {loading ? "Assigning..." : "Assign Task"}
        </button>
      </form>

      <div className="assignments-section">
        {form.task_id ? (
          assignments.length === 0 ? (
            <p className="no-assignments">
              No employees assigned to this task.
            </p>
          ) : (
            <>
              <h3>Current Assignments</h3>
              <table className="assignments-table" role="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Assigned By</th>
                    <th>Assigned At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <tr key={a.employee_id}>
                      <td>{a.employee_name || a.employee_id}</td>
                      <td>{a.role_in_task || "-"}</td>
                      <td>{a.assigned_by_name || a.assigned_by || "-"}</td>
                      <td>
                        {a.assigned_at
                          ? new Date(a.assigned_at).toLocaleString()
                          : "-"}
                      </td>
                      <td>
                        <button
                          onClick={() => handleRemove(a.task_id, a.employee_id)}
                        >
                          ❌ Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )
        ) : (
          <p className="no-assignments">
            Select a task to view current assignments.
          </p>
        )}
      </div>
    </div>
  );
}

export default AssignTask;
